/* Kalve homepage behaviour: <kalve-video> (lazy load + play/pause + fade-in over the poster; pauses while a sticky panel is fully covered by the next section).
   Safe to include more than once (every section loads it with defer); definitions are guarded. No third-party requests. */
(function () {
  'use strict';

  var mqPhone = window.matchMedia('(max-width: 900px)');
  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  function onChange(mq, fn) { if (mq.addEventListener) mq.addEventListener('change', fn); else if (mq.addListener) mq.addListener(fn); }
  function offChange(mq, fn) { if (mq.removeEventListener) mq.removeEventListener('change', fn); else if (mq.removeListener) mq.removeListener(fn); }
  function saveData() { return !!(navigator.connection && navigator.connection.saveData); }

  /* ---- "covered" check for pinned panels ----
     The panel's section wrapper is position: sticky (kalve-home.css) and the next section scrolls up over it. Once that next section's top
     has reached the panel's top, the panel is fully hidden: pause the clip (a covered video would keep decoding for nothing) and resume
     when it is uncovered again. One passive scroll / resize listener, throttled with requestAnimationFrame, one rect read per panel. */
  var panels = [];
  var ticking = false;
  function checkCovered() {
    ticking = false;
    for (var i = 0; i < panels.length; i++) panels[i]._checkCovered();
  }
  function queueCheck() {
    if (ticking) return;
    ticking = true;
    (window.requestAnimationFrame || window.setTimeout)(checkCovered);
  }
  function addPanel(el) {
    if (panels.indexOf(el) === -1) panels.push(el);
    if (panels.length === 1) {
      window.addEventListener('scroll', queueCheck, { passive: true });
      window.addEventListener('resize', queueCheck, { passive: true });
    }
  }
  function removePanel(el) {
    var i = panels.indexOf(el);
    if (i !== -1) panels.splice(i, 1);
    if (!panels.length) {
      window.removeEventListener('scroll', queueCheck);
      window.removeEventListener('resize', queueCheck);
    }
  }

  if (!customElements.get('kalve-video')) {
    class KalveVideo extends HTMLElement {
      constructor() {
        super();
        this._visible = false;
        this._covered = false;
        this._sync = this._sync.bind(this);
        this._onLoaded = this._onLoaded.bind(this);
        this._onMeta = this._onMeta.bind(this);
        this._onError = this._onError.bind(this);
      }

      connectedCallback() {
        this._wrap = this.closest('.shopify-section');
        var sec = this.closest('.k-vsec');
        this._cap = sec && sec.querySelector('.k-cap');
        if (this._wrap) addPanel(this);
        this._checkCovered();

        this._video = this.querySelector('video');
        if (!this._video) return;
        this._desktop = this.getAttribute('data-src-desktop') || '';
        this._mobile = this.getAttribute('data-src-mobile') || '';
        if (!this._desktop && !this._mobile) return;

        this._video.addEventListener('loadedmetadata', this._onMeta);
        this._video.addEventListener('loadeddata', this._onLoaded);
        this._video.addEventListener('error', this._onError);
        onChange(mqPhone, this._sync);
        onChange(mqReduce, this._sync);

        if ('IntersectionObserver' in window) {
          this._io = new IntersectionObserver(function (entries) {
            this._visible = entries[entries.length - 1].isIntersecting;
            this._sync();
          }.bind(this), { rootMargin: '200px' });
          this._io.observe(this);
        } else {
          this._visible = true;
        }

        /* first screen (hero): start loading at once; everything else waits until it is near the viewport */
        if (this.hasAttribute('data-eager')) this._load();
        this._sync();
      }

      disconnectedCallback() {
        if (this._io) this._io.disconnect();
        removePanel(this);
        offChange(mqPhone, this._sync);
        offChange(mqReduce, this._sync);
        if (this._video) {
          this._video.removeEventListener('loadedmetadata', this._onMeta);
          this._video.removeEventListener('loadeddata', this._onLoaded);
          this._video.removeEventListener('error', this._onError);
          this._video.pause();
        }
      }

      /* covered = the pinned (sticky) wrapper's next sibling has scrolled up to the wrapper's top edge.
         lift = how far that next section has slid over the panel: the buttons ride up with its top edge (they stay on the part of the film
         still showing), then fade out as they reach the header. */
      _checkCovered() {
        var wrap = this._wrap;
        var next = wrap && wrap.nextElementSibling;
        var covered = false, lift = 0;
        if (next && window.getComputedStyle(wrap).position === 'sticky') {
          var wr = wrap.getBoundingClientRect(), nTop = next.getBoundingClientRect().top;
          covered = nTop <= wr.top + 1;
          lift = Math.max(0, Math.min(wr.height, wr.bottom - nTop));
        }
        if (this._cap) this._liftCap(lift);
        if (covered !== this._covered) {
          this._covered = covered;
          this._sync();
        }
      }

      _liftCap(lift) {
        var cap = this._cap;
        var prev = this._lift || 0;
        if (lift !== prev) {
          cap.style.transform = lift ? 'translateY(' + -lift + 'px)' : '';
          this._lift = lift;
        }
        var hdr = document.querySelector('.k-section-header');
        var hdrBottom = hdr ? Math.max(0, hdr.getBoundingClientRect().bottom) : 0;
        var gap = cap.getBoundingClientRect().top - hdrBottom;
        var op = Math.max(0, Math.min(1, gap / 48));
        cap.style.opacity = op < 1 ? String(op) : '';
        cap.style.visibility = op === 0 ? 'hidden' : '';
      }

      _allowed() { return !mqReduce.matches && !saveData(); }
      _source() { return mqPhone.matches ? (this._mobile || this._desktop) : (this._desktop || this._mobile); }

      _load() {
        var src = this._source();
        if (!src || !this._allowed()) return;
        if (this._video.getAttribute('src') !== src) {
          this._video.classList.remove('is-on');
          this.classList.remove('is-playing');
          this._video.src = src;
        }
      }

      _sync() {
        if (!this._video) return;
        if (!this._allowed()) {
          /* reduced motion / data saver: keep the poster, no autoplay */
          this._video.pause();
          this._video.classList.remove('is-on');
          this.classList.remove('is-playing');
          return;
        }
        if (this._visible && !this._covered) {
          this._load();
          if (this._video.getAttribute('src')) {
            var p = this._video.play();
            if (p && p.catch) p.catch(function () {});
          }
        } else {
          this._video.pause();
        }
      }

      /* framing: landscape clips (>= 1.5) fill the panel (cover); portrait / square clips are shown whole (contain, blurred poster behind)
         except on phones, where CSS always fills the panel. */
      _onMeta() {
        var w = this._video.videoWidth, h = this._video.videoHeight;
        var sec = this.closest('.k-vsec');
        if (!sec || !w || !h) return;
        sec.classList.toggle('k-vsec--cover', w / h >= 1.5);
      }

      _onLoaded() {
        this._onMeta();
        this._video.classList.add('is-on');
        this.classList.add('is-playing');
      }

      _onError() {
        this._video.classList.remove('is-on');
        this.classList.remove('is-playing');
      }
    }
    customElements.define('kalve-video', KalveVideo);
  }
})();
